/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ======================================================================
 * Doonails — Add-to-cart "sold out" recovery
 * ----------------------------------------------------------------------
 * Why: the inventory-sync app occasionally leaves Shopify believing a
 * variant is sold out although it is in stock. The page still shows the
 * product as available, but POST /cart/add.js answers 422 with
 * "Das Produkt '…' ist bereits ausverkauft." (localised by Shopify).
 *
 * What: intercepts every add-to-cart response on the page (theme form,
 * set/bundle builders, upsell pop-ups, Replo and other app scripts that use
 * fetch) and, when the 422 text says "sold out", clears Cache API storage +
 * localStorage + sessionStorage and reloads the page so it renders from
 * fresh data.
 *
 * Guards:
 *  - only after a real user action (click/tap/key/submit within
 *    USER_ACTION_WINDOW_MS) — never for automatic adds such as free gifts
 *    (_isGift/_freeAt), checkout gifts (_gift) or the mandatory-item guard
 *  - never touches cookies (Shopify's cart + consent cookies must survive)
 *  - one recovery per page load, and not again for the same variant within
 *    RECOVERY_COOLDOWN_MS → no reload loop when the variant really is gone;
 *    the page is only reloaded once that cooldown marker verifiably persisted
 *  - reacts to sold-out texts only, not "maximum quantity in cart" etc.
 *  - PRESERVE_* entries survive the wipe: they are state, not caches
 *    (pre-access password gate, GrowthBook customer type, A/B bucket,
 *    Ablyft/Klaviyo identity, the sale colour-chooser selection)
 *  - each recovery is reported to Sentry (warning) on the page load that
 *    follows the reload, so the event is not lost to the lazy Sentry loader
 *
 * Optional (off by default, see RETRY_ONCE_BEFORE_RELOAD): re-send the
 * identical request once after RETRY_DELAY_MS; if that succeeds the caller
 * receives the success response and nothing is cleared or reloaded.
 * ====================================================================== */
(function () {
  'use strict';
  if (window.__dnAtcSoldOutRecovery) return;
  window.__dnAtcSoldOutRecovery = true;

  var RETRY_ONCE_BEFORE_RELOAD = false;      // user-requested behaviour = false
  var RETRY_DELAY_MS = 400;
  var RECOVERY_COOLDOWN_MS = 10 * 60 * 1000; // per variant, survives the reload
  var USER_ACTION_WINDOW_MS = 8000;          // the 422 must follow a user gesture this closely
  var CACHE_CLEAR_TIMEOUT_MS = 300;          // never let the Cache API delay the reload
  // Entries that survive the wipe — state, not caches (set all three to [] to wipe everything):
  var PRESERVE_KEYS = ['lp-password', 'dn_bought', 'abTestClass']; // localStorage, exact
  var PRESERVE_PREFIXES = ['ablyft', 'klaviyo', '__kla'];           // localStorage, prefix (A/B buckets + identity)
  var PRESERVE_SESSION_KEYS = ['salePdpVariant'];                    // sessionStorage, exact (sale colour-chooser flow)
  var MARKER_KEY = 'dn:atc-soldout-recovery';
  var MARKER_COOKIE = 'dn_atc_soldout_recovery';
  var ADD_TO_CART_PATH_RE = /(^|\/)cart\/add(\.js)?$/;
  // Shopify's localised "already sold out" wording for the languages the six Doonails
  // storefronts publish (de/ch: ausverkauft · en: sold out · nl: uitverkocht · fr: épuisé ·
  // it: esaurito; es/pt are spares). The vendor locales/ folder is NOT the published set.
  // Deliberately does NOT match "maximum quantity … already in your cart" / "Only N … were added".
  var SOLD_OUT_RE = /ausverkauft|sold out|uitverkocht|[eé]puis[eé]|esaurit[oa]|agotad[oa]|esgotad[oa]/i;
  // Line-item properties used by the theme's automatic adds (free gift, checkout gift).
  var AUTOMATIC_ADD_RE = /_isGift|_freeAt|_gift\b/;

  var originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;
  var recoveredThisPage = false;
  var lastUserActionAt = 0;

  ['pointerdown', 'click', 'keydown', 'submit'].forEach(function (type) {
    document.addEventListener(type, function () { lastUserActionAt = Date.now(); }, true);
  });
  function followsUserAction() { return Date.now() - lastUserActionAt < USER_ACTION_WINDOW_MS; }

  function pathOf(input) {
    try {
      var raw = typeof input === 'string' ? input : (input && input.url) || '';
      return new URL(raw, window.location.href).pathname;
    } catch (e) { return ''; }
  }

  function isAddToCart(input) { return ADD_TO_CART_PATH_RE.test(pathOf(input)); }

  function soldOutText(json) {
    if (!json || typeof json !== 'object') return '';
    var candidates = [json.description, json.message].filter(function (s) { return typeof s === 'string'; });
    for (var i = 0; i < candidates.length; i++) if (SOLD_OUT_RE.test(candidates[i])) return candidates[i];
    return '';
  }

  // Flatten the request body to [key, value] pairs so ids and markers can be read
  // regardless of FormData / URLSearchParams / JSON / urlencoded encoding. Never consumes the body.
  function bodyEntries(init) {
    var entries = [];
    try {
      var body = init && init.body;
      if (!body) return entries;
      if ((typeof FormData !== 'undefined' && body instanceof FormData) ||
          (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)) {
        body.forEach(function (v, k) { entries.push([k, typeof v === 'string' ? v : '']); });
      } else if (typeof body === 'string') {
        var text = body.trim();
        if (text.charAt(0) === '{') {
          var json = JSON.parse(text);
          var items = Array.isArray(json.items) ? json.items : [json];
          items.forEach(function (it) {
            if (!it || typeof it !== 'object') return;
            if (it.id != null) entries.push(['id', String(it.id)]);
            if (it.properties && typeof it.properties === 'object') {
              Object.keys(it.properties).forEach(function (p) { entries.push(['properties[' + p + ']', String(it.properties[p])]); });
            }
          });
        } else {
          new URLSearchParams(text).forEach(function (v, k) { entries.push([k, v]); });
        }
      }
    } catch (e) { /* unknown body shape — entries stay empty */ }
    return entries;
  }

  function extractVariantIds(entries) {
    var ids = [];
    entries.forEach(function (kv) {
      if (kv[0] === 'id' || /^items\[\d+\]\[id\]$/.test(kv[0])) {
        var n = parseInt(kv[1], 10);
        if (n > 0 && ids.indexOf(n) === -1) ids.push(n);
      }
    });
    return ids;
  }

  function isAutomaticAdd(entries) {
    return entries.some(function (kv) { return AUTOMATIC_ADD_RE.test(kv[0]); });
  }

  // ---- cooldown marker: sessionStorage, cookie as fallback when storage is unavailable
  function readMarker() {
    try { var s = sessionStorage.getItem(MARKER_KEY); if (s) return JSON.parse(s); } catch (e) {}
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + MARKER_COOKIE + '=([^;]*)'));
      if (m) return JSON.parse(decodeURIComponent(m[1]));
    } catch (e) {}
    return null;
  }
  function writeMarker(marker) {
    var value = JSON.stringify(marker);
    var stored = false;
    try { sessionStorage.setItem(MARKER_KEY, value); stored = sessionStorage.getItem(MARKER_KEY) === value; } catch (e) {}
    if (!stored) {
      try {
        document.cookie = MARKER_COOKIE + '=' + encodeURIComponent(value) +
          '; max-age=' + Math.round(RECOVERY_COOLDOWN_MS / 1000) + '; path=/; SameSite=Lax' +
          (location.protocol === 'https:' ? '; Secure' : '');
      } catch (e) {}
    }
  }
  function recentlyRecovered(ids) {
    var marker = readMarker();
    if (!marker || typeof marker.ts !== 'number') return false;
    if (Date.now() - marker.ts > RECOVERY_COOLDOWN_MS) return false;
    if (!ids.length || !Array.isArray(marker.ids) || !marker.ids.length) return true; // unknown ids → be conservative
    return ids.some(function (id) { return marker.ids.indexOf(id) !== -1; });
  }

  // ---- clearing
  // Cache API: no service worker is registered on this storefront (checked 2026-09), so this is a
  // no-op safety net; the reload itself revalidates the document. The HTTP cache cannot be cleared from JS.
  function clearCacheStorage() {
    if (!window.caches || typeof caches.keys !== 'function') return Promise.resolve();
    var work = caches.keys()
      .then(function (keys) { return Promise.all(keys.map(function (k) { return caches.delete(k); })); })
      .catch(function () {});
    var timeout = new Promise(function (resolve) { setTimeout(resolve, CACHE_CLEAR_TIMEOUT_MS); });
    return Promise.race([work, timeout]);
  }
  function preserveLocal(key) {
    if (PRESERVE_KEYS.indexOf(key) !== -1) return true;
    return PRESERVE_PREFIXES.some(function (prefix) { return key.indexOf(prefix) === 0; });
  }
  function preserveSession(key) { return PRESERVE_SESSION_KEYS.indexOf(key) !== -1; }
  function snapshot(storage, predicate, keep) {
    try {
      for (var i = 0; i < storage.length; i++) {
        var key = storage.key(i);
        if (typeof key === 'string' && predicate(key)) keep.push([storage, key, storage.getItem(key)]);
      }
    } catch (e) {}
  }
  function clearWebStorage() {
    var keep = [];
    try { snapshot(localStorage, preserveLocal, keep); } catch (e) {}
    try { snapshot(sessionStorage, preserveSession, keep); } catch (e) {}
    try { localStorage.clear(); } catch (e) {}
    try { sessionStorage.clear(); } catch (e) {}
    keep.forEach(function (entry) { try { entry[0].setItem(entry[1], entry[2]); } catch (e) {} });
  }

  function report(phase, ids, text) {
    try {
      if (!window.Sentry || typeof window.Sentry.captureMessage !== 'function') return;
      window.Sentry.captureMessage('ATC sold-out recovery: ' + phase, {
        level: 'warning',
        tags: { dn_feature: 'atc-soldout-recovery', dn_phase: phase },
        extra: { variant_ids: ids, shopify_description: text, page: location.pathname + location.search }
      });
    } catch (e) {}
  }

  // A recovery is reported on the page load that follows the reload: the Sentry SDK is
  // lazy-loaded and an event queued right before location.reload() would be lost.
  (function reportPendingRecovery() {
    var marker = readMarker();
    if (!marker || marker.reported !== false) return;
    report('clear-and-reload', marker.ids || [], marker.text || '');
    marker.reported = true;
    writeMarker(marker);
  })();

  function recover(ids, text) {
    if (recoveredThisPage) return Promise.resolve(); // concurrent 422s: first one wins
    recoveredThisPage = true;
    return clearCacheStorage().then(function () {
      clearWebStorage();
      var marker = { ts: Date.now(), ids: ids, text: text, reported: false };
      writeMarker(marker); // after the clear, so the cooldown survives the reload
      var persisted = readMarker();
      if (!persisted || persisted.ts !== marker.ts) return; // nowhere to remember this → never risk a reload loop
      window.location.reload();
    });
  }

  function retryOnce(input, init) {
    return new Promise(function (resolve) { setTimeout(resolve, RETRY_DELAY_MS); })
      .then(function () { return originalFetch.call(window, input, init); });
  }

  var dnFetchWithSoldOutRecovery = function dnFetchWithSoldOutRecovery(input, init) {
    var isAtc = isAddToCart(input);
    var guardRunning = !!window.__dnGuardRunning; // cart-mandatory-guard / checkout-gift-popup add in flight (read at call time)
    // A Request object's body can only be read once — keep a clone for the optional retry.
    var retryInput = (isAtc && RETRY_ONCE_BEFORE_RELOAD && typeof Request !== 'undefined' && input instanceof Request)
      ? input.clone() : input;
    var pending = originalFetch.apply(this, arguments);
    if (!isAtc) return pending;

    return pending.then(function (response) {
      if (!response || response.status !== 422 || recoveredThisPage || guardRunning || !followsUserAction()) return response;
      var entries = bodyEntries(init);
      if (isAutomaticAdd(entries)) return response;
      return response.clone().json().then(function (json) {
        var text = soldOutText(json);
        if (!text) return response;
        var ids = extractVariantIds(entries);
        if (recentlyRecovered(ids)) { report('cooldown-skip', ids, text); return response; }
        if (!RETRY_ONCE_BEFORE_RELOAD) return recover(ids, text).then(function () { return response; });
        return retryOnce(retryInput, init).then(function (second) {
          if (second && second.ok) { report('retry-succeeded', ids, text); return second; }
          return recover(ids, text).then(function () { return response; });
        }, function () {
          return recover(ids, text).then(function () { return response; });
        });
      }, function () { return response; }); // not JSON → not ours
    });
  };
  /* DN (Sentry DE-1X5): in manchen Umgebungen (Browser-Erweiterung, In-App-Browser) ist window.fetch nicht
     ueberschreibbar und die Zuweisung wirft "Cannot assign to read only property 'fetch'". Dann bleibt die
     Sold-out-Recovery einfach aus, der Shop laeuft mit dem normalen fetch weiter. */
  try {
    window.fetch = dnFetchWithSoldOutRecovery;
  } catch (e) {}
})();

/* ======================================================================
 * Doonails — one per order (product tag `dn-max-1`)
 * ----------------------------------------------------------------------
 * Why: sample products such as the Formfinder flyer (handle formfinder-flyer)
 * go out once per order. Shopify has no per-product quantity cap for B2C
 * carts, and POST /cart/add.js merges a repeated add into the existing line,
 * so a second click silently makes the quantity 2.
 *
 * What: every product tagged `dn-max-1` is capped at quantity 1 per cart,
 * counted across all of its variants and lines.
 *  - Liquid removes the quantity controls: snippets/dn-product-form.liquid
 *    (PDP), sections/mini-cart.liquid (drawer), sections/main-cart.liquid
 *    (cart page). The drawer also stamps every rendered line with
 *    data-dn-handle and capped lines with data-dn-max-one, so this script
 *    knows the tag of everything in the cart without a request.
 *  - This script is the safety net for every add path (theme form, sticky
 *    bars, upsell pop-ups, landing pages, app scripts, direct URLs):
 *    1. fetch add requests are trimmed before they leave the page: quantity
 *       becomes 1, an item whose product is already in the cart is dropped,
 *       and a request that would add nothing is answered 422 the way Shopify
 *       answers a cart error (the theme shows the description as a notice);
 *    2. after any cart mutation (fetch or XHR) the cart is re-read and, when
 *       the cap is still exceeded (XHR adds, /cart/add?... links, other tabs),
 *       the surplus is removed with /cart/change.js and the drawer redrawn;
 *    3. on the product's own page the add-to-cart button reads "Already in
 *       cart", carries aria-disabled and ignores clicks while the product is
 *       in the cart. It is NOT set to disabled: the sticky bars
 *       (snippets/dn-single-sticky-cta.liquid, sections/dn-pdp-sticky-cta.liquid)
 *       read a disabled button as sold out and become the Klaviyo "notify me"
 *       proxy; they check data-dn-max-one-locked first and disable themselves.
 *
 * Products not known from the drawer markup are looked up once per page via
 * /products/<handle>.js (the pattern of snippets/cart-mandatory-guard.liquid).
 * Our own requests use the original fetch and run under `running`, so they
 * never re-trigger this guard; the sold-out recovery above only looks at
 * add.js, and cart-mandatory-guard's follow-up enforce is idempotent.
 * ====================================================================== */
(function () {
  'use strict';
  if (window.__dnMaxOneGuard) return;
  window.__dnMaxOneGuard = true;

  var TAG = 'dn-max-1';
  var CART_ADD_RE = /(^|\/)cart\/add(\.js)?$/;
  var CART_MUTATION_RE = /(^|\/)cart\/(add|change|update|clear)(\.js)?$/;
  var ENFORCE_DELAY_MS = 80;
  var LOCK_ATTR = 'data-dn-max-one-locked';

  var originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;

  function themeVars() { return window.themeVariables || {}; }
  function routes() { return themeVars().routes || {}; }
  function rootPath() { return routes().rootUrlWithoutSlash || ''; }
  function text(key, fallback) {
    var strings = themeVars().strings || {};
    return typeof strings[key] === 'string' && strings[key] ? strings[key] : fallback;
  }
  // { productId, handle, variantIds } on a capped product's own page (snippets/js-variables.liquid)
  function pageConfig() {
    var config = themeVars().dnMaxOne;
    return config && config.productId ? config : null;
  }

  var cart = null;        // last known cart, /cart.js shape
  var tagCache = {};      // handle -> boolean, or a pending Promise<boolean>
  var running = false;    // our own cart writes are in flight
  var enforceTimer = null;

  // ---- cart snapshot ---------------------------------------------------------
  function rememberCart(candidate) {
    if (candidate && Array.isArray(candidate.items)) cart = candidate;
    return cart;
  }
  try {
    var embedded = document.getElementById('cart_data'); // layout/theme.liquid renders {{ cart | json }}
    if (embedded) rememberCart(JSON.parse(embedded.textContent));
  } catch (e) { /* no embedded cart on this page */ }

  function jsonRequest(url, init) {
    var options = init || {};
    options.credentials = options.credentials || 'same-origin';
    options.headers = options.headers || {};
    if (!options.headers.Accept) options.headers.Accept = 'application/json';
    return originalFetch.call(window, url, options).then(function (response) {
      if (!response.ok) throw new Error(url + ' -> ' + response.status);
      return response.json();
    });
  }
  function readCart() {
    return jsonRequest((routes().cartUrl || '/cart') + '.js').then(rememberCart);
  }

  // ---- which products are capped ----------------------------------------------
  /* Sentry 2026-09-19 (DE-201): Formular-IDs mit Zeilenumbruch (main-article-v1) machten den Attribut-Selektor ungueltig -
     CSS.escape maskiert alles, der Rueckfall ersetzt Steuerzeichen durch Leerzeichen */
  function selectorSafe(value) {
    value = String(value);
    if (window.CSS && typeof window.CSS.escape === 'function') { return window.CSS.escape(value); }
    return value.replace(/["\\]/g, '\\$&').replace(/[\r\n\f]/g, ' ');
  }
  function knownFromDrawer(handle) {
    var line = document.querySelector('line-item[data-dn-handle="' + selectorSafe(handle) + '"]');
    return line ? line.hasAttribute('data-dn-max-one') : null;
  }
  // Answer without a request: page config, drawer markup or cache. null when unknown.
  function isCappedSync(handle) {
    if (!handle) return false;
    var page = pageConfig();
    if (page && page.handle === handle) return true;
    if (typeof tagCache[handle] === 'boolean') return tagCache[handle];
    var fromDrawer = knownFromDrawer(handle);
    if (fromDrawer !== null) tagCache[handle] = fromDrawer;
    return fromDrawer;
  }
  function isCapped(handle) {
    var known = isCappedSync(handle);
    if (known !== null) return Promise.resolve(known);
    if (!tagCache[handle]) {
      tagCache[handle] = jsonRequest(rootPath() + '/products/' + encodeURIComponent(handle) + '.js')
        .then(function (product) {
          var tags = product && Array.isArray(product.tags) ? product.tags : [];
          var capped = tags.some(function (tag) { return String(tag).trim().toLowerCase() === TAG; });
          tagCache[handle] = capped;
          return capped;
        })
        .catch(function (error) {
          // 404 = not a storefront product (hidden/unpublished): remember, do not ask again. Other errors may retry.
          if (/-> 404$/.test(String(error && error.message))) tagCache[handle] = false; else delete tagCache[handle];
          return false;
        });
    }
    return tagCache[handle];
  }

  // ---- enforce the cap on the live cart -----------------------------------------
  function changeLine(op, retried) {
    return originalFetch.call(window, (routes().cartChangeUrl || '/cart/change') + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(op)
    }).then(function (response) {
      if (response.status === 429 && !retried) { // rate limited: wait once, retry once
        return new Promise(function (resolve) { setTimeout(resolve, 600); }).then(function () { return changeLine(op, true); });
      }
      if (!response.ok) throw new Error('change.js ' + response.status);
      return response.json();
    });
  }
  // The first line of a capped product keeps quantity 1; every further line of it goes.
  function surplusOps(items) {
    return Promise.all(items.map(function (item) {
      return isCapped(item.handle).then(function (capped) { return capped ? item : null; });
    })).then(function (flags) {
      var seen = {}, ops = [];
      flags.filter(Boolean).forEach(function (item) {
        var productId = String(item.product_id);
        if (!seen[productId]) {
          seen[productId] = true;
          if (item.quantity > 1) ops.push({ id: item.key, quantity: 1 });
        } else {
          ops.push({ id: item.key, quantity: 0 });
        }
      });
      return ops;
    });
  }
  function redraw(fresh) {
    rememberCart(fresh);
    if (typeof window.dnCartNeuZeichnen === 'function') {
      // dn-cart.js: re-fetches the drawer section, invalidates older pending renders, fires cart:updated
      return window.dnCartNeuZeichnen(cart).then(syncButtons);
    }
    // theme fallback: cart:refresh makes <cart-drawer> re-fetch its section
    window.dnCartRenderVersion = (window.dnCartRenderVersion || 0) + 1;
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
    return Promise.resolve();
  }
  var recheck = false; // a mutation finished while we were trimming: look again afterwards
  function enforce() {
    if (running) { recheck = true; return Promise.resolve(); }
    return readCart()
      .then(function (current) { return surplusOps(current.items || []); })
      .then(function (ops) {
        if (!ops.length) { syncButtons(); return null; }
        running = true;
        return ops.reduce(function (chain, op) {
          return chain.then(function () { return changeLine(op); });
        }, Promise.resolve()).then(function (fresh) {
          running = false;
          return redraw(fresh);
        }, function (error) {
          running = false;
          console.warn('[dn-max-one] could not trim a cart line:', error);
          return readCart().then(redraw); // show whatever state the cart is in now
        }).then(function () {
          if (recheck) { recheck = false; scheduleEnforce(); }
        });
      })
      .catch(function (error) {
        running = false;
        console.warn('[dn-max-one] cart check failed:', error);
      });
  }
  function scheduleEnforce() {
    clearTimeout(enforceTimer);
    enforceTimer = setTimeout(enforce, ENFORCE_DELAY_MS);
  }

  // ---- trim add requests before they leave the page ---------------------------------
  function pathOf(input) {
    try {
      var raw = typeof input === 'string' ? input : (input && (input.url || input.href)) || ''; // Request or URL object
      return new URL(raw, window.location.href).pathname;
    } catch (e) { return ''; }
  }
  // What we can vouch for right now, without a request:
  // capped variant id -> product id, and the capped product ids already in the cart.
  function cappedNow() {
    var variantToProduct = {}, cappedProducts = {}, inCart = {};
    var page = pageConfig();
    var items = (cart && cart.items) || [];
    if (page) {
      cappedProducts[String(page.productId)] = true;
      (page.variantIds || []).forEach(function (id) { variantToProduct[String(id)] = String(page.productId); });
    }
    items.forEach(function (item) {
      if (isCappedSync(item.handle) === true) {
        cappedProducts[String(item.product_id)] = true;
        variantToProduct[String(item.variant_id)] = String(item.product_id);
      }
    });
    items.forEach(function (item) {
      if (cappedProducts[String(item.product_id)]) inCart[String(item.product_id)] = true;
    });
    return { variantToProduct: variantToProduct, inCart: inCart };
  }
  function toQuantity(value) {
    if (value === undefined || value === null || value === '') return 1;
    var n = parseInt(value, 10);
    return isNaN(n) ? 1 : n;
  }
  // The add bodies used on this storefront. Returns { items: [{ id, quantity }], rebuild(items) -> body } or null.
  function readAddBody(init) {
    var body = init && init.body;
    if (body === undefined || body === null) return null;
    var isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    var isParams = typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams;
    if (isFormData || isParams) {
      var multi = false;
      body.forEach(function (value, key) { if (/^items\[/.test(key)) multi = true; });
      if (multi) return null; // multi-item form encoding: left to enforce()
      var id = body.get('id');
      if (id === null || id === '') return null;
      return {
        items: [{ id: String(id), quantity: toQuantity(body.get('quantity')) }],
        rebuild: function (items) {
          var copy = isFormData ? new FormData() : new URLSearchParams();
          body.forEach(function (value, key) { copy.append(key, value); });
          copy.set('quantity', String(items[0].quantity));
          return copy;
        }
      };
    }
    if (typeof body !== 'string') return null;
    var trimmed = body.trim();
    if (trimmed.charAt(0) === '{') {
      var json = JSON.parse(trimmed);
      if (Array.isArray(json.items)) {
        return {
          items: json.items.map(function (item) {
            return { id: String(item && item.id), quantity: toQuantity(item && item.quantity), raw: item };
          }),
          rebuild: function (items) {
            json.items = items.map(function (item) { item.raw.quantity = item.quantity; return item.raw; });
            return JSON.stringify(json);
          }
        };
      }
      if (json.id === undefined || json.id === null) return null;
      return {
        items: [{ id: String(json.id), quantity: toQuantity(json.quantity) }],
        rebuild: function (items) { json.quantity = items[0].quantity; return JSON.stringify(json); }
      };
    }
    var params = new URLSearchParams(trimmed);
    var paramId = params.get('id');
    if (paramId === null || paramId === '') return null;
    return {
      items: [{ id: paramId, quantity: toQuantity(params.get('quantity')) }],
      rebuild: function (items) { params.set('quantity', String(items[0].quantity)); return params.toString(); }
    };
  }
  // null = send as is; { blocked: true } = answer 422; { init } = send with the trimmed body
  function trimAdd(init) {
    var parsed = readAddBody(init);
    if (!parsed || !parsed.items.length) return null;
    var caps = cappedNow();
    var keep = [], seenProduct = {}, changed = false;
    parsed.items.forEach(function (item) {
      var productId = caps.variantToProduct[item.id];
      if (!productId) { keep.push(item); return; }
      if (caps.inCart[productId] || seenProduct[productId]) { changed = true; return; } // already there: drop
      seenProduct[productId] = true;
      if (item.quantity !== 1) { item.quantity = 1; changed = true; }
      keep.push(item);
    });
    if (!changed) return null;
    if (!keep.length) return { blocked: true };
    return { init: Object.assign({}, init, { body: parsed.rebuild(keep) }) };
  }
  function blockedResponse() {
    var payload = {
      status: 422,
      message: 'Cart Error',
      description: text('cartMaxOneError', 'You can add this product only once per order.')
    };
    return new Response(JSON.stringify(payload), {
      status: 422,
      statusText: 'Unprocessable Entity',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function decide(init) {
    try { return trimAdd(init); } catch (e) { return null; } // unknown body: send as is
  }
  function send(input, init, mutation) {
    var pending = init === undefined ? originalFetch.call(window, input) : originalFetch.call(window, input, init);
    if (mutation) pending.then(function (response) { if (response && response.ok) scheduleEnforce(); }, function () {});
    return pending;
  }
  var dnFetchWithMaxOne = function dnFetchWithMaxOne(input, init) {
    var path = pathOf(input);
    var mutation = CART_MUTATION_RE.test(path);
    if (!mutation || !CART_ADD_RE.test(path) || typeof Response !== 'function') return send(input, init, mutation);
    var decision = decide(init);
    if (decision && decision.blocked) {
      // The snapshot may be stale (item removed in another tab): confirm against the live cart before refusing.
      return readCart().then(function () {
        var fresh = decide(init);
        if (fresh && fresh.blocked) return blockedResponse();
        return send(input, fresh && fresh.init ? fresh.init : init, true);
      }, function () { return send(input, init, true); });
    }
    return send(input, decision && decision.init ? decision.init : init, true);
  };

  // XHR paths (jQuery / app scripts): no request trimming, but every successful cart write is checked.
  if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;
    try {
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__dnMaxOnePath = pathOf(url === undefined || url === null ? '' : String(url));
        return xhrOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        if (CART_MUTATION_RE.test(xhr.__dnMaxOnePath || '')) {
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) scheduleEnforce();
          });
        }
        return xhrSend.apply(this, arguments);
      };
    } catch (e) { /* prototype not writable: the fetch paths are still covered */ }
  }

  // ---- product page: inert "Already in cart" button -----------------------------------
  var STYLE_ID = 'dn-max-one-style';
  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '[' + LOCK_ATTR + ']{pointer-events:none;cursor:not-allowed;opacity:.6}' +
      '[' + LOCK_ATTR + '] .dn-max-one-original{display:none!important}';
    (document.head || document.documentElement).appendChild(style);
  }
  function labelHost(button) { return button.querySelector('.loader-button__text') || button; }
  function lock(button) {
    var host = labelHost(button);
    if (button.hasAttribute(LOCK_ATTR) && host.querySelector('.dn-max-one-label')) return;
    // keep the original label (with the price spans other scripts write to) in the DOM, just hidden
    var original = document.createElement('span');
    original.className = 'dn-max-one-original';
    original.setAttribute('aria-hidden', 'true');
    original.hidden = true;
    while (host.firstChild) original.appendChild(host.firstChild);
    var label = document.createElement('span');
    label.className = 'dn-max-one-label';
    label.textContent = text('productFormAlreadyInCart', 'Already in cart');
    host.appendChild(original);
    host.appendChild(label);
    button.setAttribute(LOCK_ATTR, ''); // before aria-disabled: the sticky bars read the lock attribute first
    button.setAttribute('aria-disabled', 'true');
  }
  function unlock(button) {
    if (!button.hasAttribute(LOCK_ATTR)) return;
    var host = labelHost(button);
    var label = host.querySelector('.dn-max-one-label');
    var original = host.querySelector('.dn-max-one-original');
    if (label) label.parentNode.removeChild(label);
    if (original) {
      while (original.firstChild) host.insertBefore(original.firstChild, original);
      original.parentNode.removeChild(original);
    }
    button.removeAttribute('aria-disabled');
    button.removeAttribute(LOCK_ATTR);
  }
  function productInCart(productId) {
    var items = (cart && cart.items) || [];
    return items.some(function (item) { return String(item.product_id) === String(productId); });
  }
  function isSubmitButton(el) {
    return el.tagName === 'BUTTON' && (el.type === 'submit' || el.hasAttribute('data-product-add-to-cart-button'));
  }
  function addButtonsOf(form) {
    var buttons = Array.prototype.filter.call(form.querySelectorAll('button'), isSubmitButton);
    // form.id is the <input name="id"> here (form controls shadow the property), so read the attribute
    var formId = form.getAttribute('id');
    if (formId) { // buttons outside the form that submit it via form="..." (snippets/product-sticky-form.liquid)
      Array.prototype.forEach.call(document.querySelectorAll('button[form="' + selectorSafe(formId) + '"]'), function (el) {
        if (isSubmitButton(el) && buttons.indexOf(el) === -1) buttons.push(el);
      });
    }
    return buttons;
  }
  function isAddForm(form) {
    return !!form && !!form.getAttribute && CART_ADD_RE.test(pathOf(form.getAttribute('action') || ''));
  }
  function syncButtons() {
    var page = pageConfig();
    if (!page) return;
    ensureStyle();
    var inCart = productInCart(page.productId);
    var variantIds = (page.variantIds || []).map(String);
    Array.prototype.forEach.call(document.querySelectorAll('form[action]'), function (form) {
      if (!isAddForm(form)) return;
      var idField = form.querySelector('[name="id"]');
      var applies = !!idField && variantIds.indexOf(String(idField.value)) !== -1;
      addButtonsOf(form).forEach(function (button) {
        if (applies && inCart) lock(button); else unlock(button);
      });
    });
  }
  function lockedAncestor(target) {
    return target && target.closest ? target.closest('[' + LOCK_ATTR + ']') : null;
  }
  document.addEventListener('click', function (event) {
    if (lockedAncestor(event.target)) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isAddForm(form)) return;
    if (addButtonsOf(form).some(function (button) { return button.hasAttribute(LOCK_ATTR); })) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  ['cart:updated', 'cart:refresh'].forEach(function (name) {
    document.documentElement.addEventListener(name, function (event) {
      rememberCart(event && event.detail && event.detail.cart);
      syncButtons();
    });
  });
  document.addEventListener('variant:changed', function () { syncButtons(); });
  // theme.js ProductRerender swaps the buy buttons (and the sticky form) after a variant change, then fires this
  document.addEventListener('doc:rerendered', function () { syncButtons(); });
  document.addEventListener('shopify:section:load', function () { syncButtons(); });
  // Back from another tab, where the item may have been removed or added: refresh the snapshot on a capped PDP.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && pageConfig()) readCart().then(syncButtons).catch(function () {});
  });

  function init() {
    syncButtons();
    // The embedded cart plus the drawer markup tell us, without a request, whether a capped
    // product already sits in the cart more than once (direct links, other tabs).
    var counts = {}, exceeded = false;
    ((cart && cart.items) || []).forEach(function (item) {
      if (isCappedSync(item.handle) !== true) return;
      var productId = String(item.product_id);
      counts[productId] = (counts[productId] || 0) + (item.quantity || 0);
      if (counts[productId] > 1) exceeded = true;
    });
    if (exceeded) enforce();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  /* Like the sold-out recovery above: window.fetch is read-only in some in-app browsers. Then only the
     Liquid controls, the XHR check and the page-load check apply. */
  try { window.fetch = dnFetchWithMaxOne; } catch (e) {}
})();
